import { LegalPage, Section } from "@/components/LegalPage";
import { Link } from "react-router-dom";

export default function Terms() {
  return (
    <LegalPage title="Terms of Service" testId="terms-title">
      <Section title="1. Acceptance of Terms">
        <p>By creating an account, claiming a Passport, or otherwise using BeatCredsi™ ("Platform"), you agree to these Terms of Service. If you do not agree, do not use the Platform. BeatCredsi™ is operated by Only Few Get Selected, LLC.</p>
      </Section>

      <Section title="2. About BeatCredsi">
        <p>BeatCredsi is the Music Infrastructure Intelligence Platform — the world's first Music Creator Infrastructure Scoring System. It generates a music-creator infrastructure score (0–1000) based on:</p>
        <ul>
          <li>Metadata Authority</li>
          <li>Royalty Infrastructure</li>
          <li>Rights Protection</li>
          <li>Catalog Operations</li>
          <li>Business Structure</li>
        </ul>
        <p>All scores, recommendations, certifications, and royalty estimates are provided for <strong>informational and educational purposes only</strong> and do not constitute legal, financial, tax, or accounting advice.</p>
      </Section>

      <Section title="3. User Accounts">
        <p>You agree to:</p>
        <ul>
          <li>Provide accurate, current, and complete information during signup and assessments.</li>
          <li>Maintain the security of your password and any session tokens.</li>
          <li>Verify your email address; unverified accounts may have limited features.</li>
          <li>Not share your account or allow others to access the Platform with your credentials.</li>
          <li>Notify us immediately at <a href="mailto:contact@onlymartian.com">contact@onlymartian.com</a> of any unauthorized use.</li>
        </ul>
      </Section>

      <Section title="4. Public BeatCredsi Passport">
        <p>Your Passport (at <code>beatcredsi.com/c/{`{username}`}</code>) is publicly accessible. You control what appears via your profile, discography links, and uploaded evidence. You acknowledge that:</p>
        <ul>
          <li>You are responsible for the accuracy of all information on your Passport.</li>
          <li>You retain ownership of content you submit, but grant BeatCredsi a worldwide, royalty-free license to display, format, and distribute it on the Platform.</li>
          <li>You may rename your username; old URLs will permanently redirect (301) to your current username.</li>
        </ul>
      </Section>

      <Section title="5. Assessments &amp; Score Calculations">
        <p>The BeatCredsi Score (0–1000) is a proprietary, algorithmic estimate based on the inputs you provide. <strong>You acknowledge that:</strong></p>
        <ul>
          <li>Scores are not credit scores, financial ratings, or industry rankings.</li>
          <li>BeatCredsi does not independently verify every claim you submit unless explicitly marked "Verified."</li>
          <li>Scores can change over time as you add infrastructure or as the scoring algorithm is updated.</li>
          <li>BeatCredsi makes no guarantee that a higher score will lead to placements, deals, royalty income, or career success.</li>
        </ul>
      </Section>

      <Section title="6. Certifications">
        <p>Certifications are awarded after passing in-platform exams (typically 80% or higher). <strong>You acknowledge that:</strong></p>
        <ul>
          <li>Certifications are issued by BeatCredsi and reflect demonstrated knowledge of the Platform's curriculum only.</li>
          <li>Certifications are not industry-recognized credentials, do not confer professional licensure, and do not guarantee employment, royalty collection, or financial outcomes.</li>
          <li>Certifications may be revoked if obtained through cheating, automation, or violation of these Terms.</li>
        </ul>
      </Section>

      <Section title="7. Royalty Estimates &amp; Tools">
        <p>The Royalty &amp; UGC Opportunity Analyzer and similar tools produce <strong>illustrative estimates only</strong>. Actual royalty income depends on streaming volume, licensing rates, collection society timelines, registrations, and many other factors outside BeatCredsi&apos;s control. BeatCredsi does not guarantee any specific royalty amount.</p>
      </Section>

      <Section title="8. No Guarantees">
        <p>BeatCredsi makes <strong>no warranty or guarantee</strong> of:</p>
        <ul>
          <li>Music placements, sync deals, or publishing agreements</li>
          <li>Royalty earnings, payouts, or recovery of unclaimed royalties</li>
          <li>Career advancement, fame, or financial success</li>
          <li>Business outcomes of any kind</li>
        </ul>
        <p>You remain solely responsible for your business decisions.</p>
      </Section>

      <Section title="9. Subscriptions &amp; Payments">
        <p>Memberships are processed by Stripe, Inc. By purchasing a subscription you agree to <Link to="/refunds" className="text-amber-500">our Refund Policy</Link> and to Stripe's terms. We do not store full card numbers; payment information is handled by Stripe.</p>
      </Section>

      <Section title="10. User-Generated Content">
        <p>You retain ownership of any content you submit (bio, photos, discography links, achievement uploads, evidence submissions). By submitting, you grant BeatCredsi a non-exclusive, royalty-free, worldwide license to host, display, reformat, and use that content for operation and promotion of the Platform.</p>
        <p>You represent and warrant that you have the right to submit each piece of content and that it does not infringe third-party rights.</p>
      </Section>

      <Section title="11. Intellectual Property">
        <p>The BeatCredsi name, logo, scoring system, course curriculum, and all platform code and design are the property of Only Few Get Selected, LLC. You may not copy, modify, redistribute, or create derivative works without written permission.</p>
      </Section>

      <Section title="12. Acceptable Use">
        <p>You must follow our <Link to="/aup" className="text-amber-500">Acceptable Use Policy</Link>. Violations may result in account suspension or termination.</p>
      </Section>

      <Section title="13. Account Suspension &amp; Termination">
        <p>We may suspend or terminate your account for breach of these Terms, fraudulent activity, or extended inactivity. You may close your account at any time by emailing <a href="mailto:contact@onlymartian.com">contact@onlymartian.com</a>.</p>
      </Section>

      <Section title="14. Disclaimer of Warranties">
        <p>The Platform is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.</p>
      </Section>

      <Section title="15. Limitation of Liability">
        <p>To the maximum extent permitted by law, Only Few Get Selected, LLC's total liability to you for any claim arising from your use of the Platform shall not exceed the total amount you paid us in the prior twelve (12) months. We are not liable for indirect, incidental, or consequential damages.</p>
      </Section>

      <Section title="16. Governing Law">
        <p>These Terms are governed by the laws of the State of Georgia, United States, without regard to its conflict-of-laws principles.</p>
      </Section>

      <Section title="17. Changes">
        <p>We may update these Terms at any time. Material changes will be announced via email or in-app notice. Continued use after changes constitutes acceptance.</p>
      </Section>

      <Section title="18. Ownership Notice">
        <p>BeatCredsi™ is independently owned and operated by <strong>Only Few Get Selected, LLC</strong> through its technology division, <strong>Smartian Tech™</strong>.</p>
        <p>This application is currently hosted using Emergent's deployment infrastructure. References to Emergent or "Made with Emergent" identify the hosting and development platform used to deploy BeatCredsi™ and do <strong>not</strong> indicate ownership of BeatCredsi™, its source code, trademarks, scoring systems, assessments, intellectual property, user data, or business operations.</p>
        <p>© 2026 Only Few Get Selected, LLC. All Rights Reserved.</p>
      </Section>

      <Section title="19. Contact">
        <p>Email <a href="mailto:contact@onlymartian.com">contact@onlymartian.com</a> for any questions.</p>
      </Section>
    </LegalPage>
  );
}
